import React from "react";

import type { LaneRendererProps } from "../lane.types";
import { GLASS_SECONDARY_TEXT_CLASS, compressStateChips, itemVisualDensity, productTone, readDirectCouplingLabel, readNumberMeta, readStringMeta, visibleLaneItems } from "./rendering";

function stateChip(label: string) {
  return <span className="rounded-full border border-cyan-300/18 bg-cyan-300/12 px-1.5 py-0.5 text-[8px] font-semibold uppercase tracking-[0.12em] text-cyan-50/86">{label}</span>;
}

function contractStateLabel(contractState: string | null | undefined) {
  switch (contractState) {
    case "proxy_stale":
      return "proxy stale";
    case "proxy_failed":
      return "proxy failed";
    case "proxy_missing":
      return "no proxy";
    case "deprecated_proxy_fallback":
      return "legacy proxy";
    case "direct_media_only":
      return "direct source";
    case "source_degraded":
      return "degraded source";
    case "offline":
      return "offline";
    case "missing":
      return "missing";
    default:
      return null;
  }
}

function contractStateChip(contractState: string | null | undefined) {
  const label = contractStateLabel(contractState);
  return label ? stateChip(label) : null;
}

export default function AudioAnalysisLaneRenderer({ lane, items, viewport, syncBadge }: LaneRendererProps): JSX.Element {
  const tone = productTone(lane.productType);
  const visibleItems = visibleLaneItems(items, viewport);
  const laneAnalysisState = readStringMeta({ ...lane, metadata: lane.metadata } as never, "analysisState");
  const laneProxyContractState = readStringMeta({ ...lane, metadata: lane.metadata } as never, "proxyContractState");
  const laneChannelLayout = readStringMeta({ ...lane, metadata: lane.metadata } as never, "channelLayout");
  const laneSampleRate = readNumberMeta({ ...lane, metadata: lane.metadata } as never, "sampleRate");
  const laneAnchoredRangeLabel = readStringMeta({ ...lane, metadata: lane.metadata } as never, "anchoredRangeLabel");
  const laneStateLabels = [
    laneAnalysisState,
    contractStateLabel(laneProxyContractState),
    laneChannelLayout,
    laneSampleRate != null ? `${Math.round(laneSampleRate)} hz` : null,
    laneAnchoredRangeLabel,
  ].filter((value): value is string => Boolean(value));
  const directCouplingLabel = readDirectCouplingLabel(lane.metadata ?? null, lane.id, viewport.lodTier);
  const compressedLaneState = compressStateChips(laneStateLabels, viewport.lodTier);
  const showBodyReason = viewport.lodTier !== "far";

  return (
    <div className="relative h-full min-h-[48px]" data-lane-product="audio_analysis" data-lane-id={lane.id}>
      <div className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-cyan-200/10" />
      {visibleItems.length > 0 ? (
        visibleItems.map(({ item, leftPx, widthPx }) => {
          const density = itemVisualDensity(widthPx);
          const analysisState = readStringMeta(item, "analysisState") ?? readStringMeta(item, "status");
          const proxyContractState = readStringMeta(item, "proxyContractState");
          const channelLayout = readStringMeta(item, "channelLayout");
          const sampleRate = readNumberMeta(item, "sampleRate");
          const confidence = readNumberMeta(item, "confidence");
          const label = "label" in item ? item.label : item.itemType === "event" ? item.eventKey : item.id;
          const itemStateLabels = [
            analysisState,
            contractStateLabel(proxyContractState),
            channelLayout,
            sampleRate != null ? `${Math.round(sampleRate)} hz` : null,
            confidence != null ? `${Math.round(confidence * 100)}%` : null,
          ].filter((value): value is string => Boolean(value));
          const compressedItemState = compressStateChips(itemStateLabels, viewport.lodTier);
          const showNearDetail = viewport.lodTier === "near" && density === "regular";
          const showCompactDetail = viewport.lodTier === "mid" || density === "compact";

          return (
            <div
              key={item.id}
              data-visual-lane-item-id={item.id}
              className="absolute top-1/2 h-10 -translate-y-1/2 overflow-hidden rounded-xl border border-cyan-300/24 bg-[linear-gradient(135deg,rgba(34,211,238,0.16),rgba(14,116,144,0.20),rgba(2,6,23,0.20))] px-3 shadow-[0_8px_20px_rgba(34,211,238,0.10)]"
              style={{ left: leftPx, width: widthPx }}
              title={label}
            >
              <div className="relative flex h-full items-start justify-between gap-2 pt-1.5">
                <div className="min-w-0">
                  {showNearDetail ? (
                    <>
                      <div className="truncate text-[11px] font-semibold text-cyan-50">{label}</div>
                      <div className="mt-0.5 flex items-center gap-1.5 overflow-hidden">
                        {analysisState ? stateChip(analysisState) : null}
                        {contractStateChip(proxyContractState)}
                        {channelLayout ? stateChip(channelLayout) : null}
                        {sampleRate != null ? stateChip(`${Math.round(sampleRate)} hz`) : null}
                        {confidence != null ? stateChip(`${Math.round(confidence * 100)}%`) : null}
                      </div>
                    </>
                  ) : showCompactDetail ? (
                    <>
                      <div className="truncate text-[11px] font-semibold text-cyan-50">{label}</div>
                      <div className="mt-0.5 flex items-center gap-1 overflow-hidden">
                        {compressedItemState.visible.map((state) => (
                          <React.Fragment key={state}>{stateChip(state)}</React.Fragment>
                        ))}
                        {compressedItemState.hiddenCount > 0 ? stateChip(`+${compressedItemState.hiddenCount} more`) : null}
                        {directCouplingLabel ? stateChip(directCouplingLabel) : null}
                      </div>
                    </>
                  ) : (
                    <div className="flex items-center gap-1.5 text-[8px] font-semibold uppercase tracking-[0.14em] text-cyan-50/82">
                      <span>ANA</span>
                      {confidence != null ? <span>{Math.round(confidence * 100)}%</span> : null}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })
      ) : (
        <div className={["min-h-[56px] rounded-2xl border px-3 py-3", tone.emptyClassName].join(" ")}>
          <div className="flex items-center gap-2 text-[11px] font-semibold">
            <span>Audio analysis is waiting on derived lane items.</span>
            {compressedLaneState.visible.map((state) => (
              <React.Fragment key={state}>{stateChip(state)}</React.Fragment>
            ))}
            {compressedLaneState.hiddenCount > 0 ? stateChip(`+${compressedLaneState.hiddenCount} more`) : null}
            {directCouplingLabel ? stateChip(directCouplingLabel) : null}
          </div>
          {showBodyReason ? (
          <div className={["mt-1 text-[10px]", GLASS_SECONDARY_TEXT_CLASS].join(" ")}>
            {syncBadge?.reason ?? (laneAnchoredRangeLabel
              ? `Read-only analysis ribbons will bind to ${laneAnchoredRangeLabel} when waveform or diagnostic items are present in lane state.`
              : "Read-only analysis ribbons will render here when waveform or diagnostic items are present in lane state.")}
          </div>
          ) : null}
        </div>
      )}
    </div>
  );
}